import React from 'react';
import { useCreatePath, useStore } from 'react-admin';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { FuneralListSearch } from './FuneralListSearch';
import { FuneralFiltersButton } from './FuneralFilterSidebar';

type Opt = { value: string; label: string };

const Segmented = ({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Opt[];
}) => (
  <span className="inline-flex overflow-hidden rounded-lg border border-border">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        className={cn(
          'px-3 py-1 text-xs font-semibold transition-colors',
          value === o.value
            ? 'bg-primary text-primary-foreground'
            : 'bg-card text-muted-foreground hover:bg-muted',
        )}
      >
        {o.label}
      </button>
    ))}
  </span>
);

const Label = ({ children }: { children: React.ReactNode }) => (
  <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
    {children}
  </span>
);

/**
 * The funerals list toolbar: a single row under the At-Need / Pre-Need tabs.
 *
 * Everything that narrows the list lives here — the two segmented toggles, free
 * text search, and the filter set behind a popover — with the one action, New
 * Funeral, pushed to the far end so it reads as an action rather than another
 * filter. Search leads the row: it is the control reached for most often, and
 * the widest net, so the narrowing reads left to right.
 *
 * The filter set used to be a permanent 250px sidebar; it is reached only
 * occasionally and was charging for that column on every page view.
 */
export const FuneralListActions = () => {
  const [owner, setOwner] = useStore<string>('funerals.owner', 'all');
  const [view, setView] = useStore<string>('funerals.view', 'all');
  const createPath = useCreatePath();

  return (
    <div className="cleo-ui flex w-full flex-wrap items-center gap-x-4 gap-y-2 py-2">
      <FuneralListSearch />

      <span className="flex items-center gap-2">
        <Label>Owner</Label>
        <Segmented
          value={owner}
          onChange={setOwner}
          options={[
            { value: 'all', label: 'All cases' },
            { value: 'mine', label: 'My cases' },
          ]}
        />
      </span>

      <span className="flex items-center gap-2">
        <Label>Show</Label>
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { value: 'all', label: 'All' },
            { value: 'need', label: 'Needs action' },
          ]}
        />
      </span>

      <FuneralFiltersButton />

      <Button asChild size="sm" className="ml-auto h-8 gap-1.5 text-xs">
        <Link to={createPath({ resource: 'funerals', type: 'create' })}>
          <Plus className="h-3.5 w-3.5" />
          New funeral
        </Link>
      </Button>
    </div>
  );
};

export default FuneralListActions;
